import { motion } from "framer-motion";
import { Building2, Lock, Calendar } from "lucide-react";
import { useLanguage } from "../i18n/LanguageProvider";

export const Experience = () => {
  const { t } = useLanguage();
  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-sm tracking-widest uppercase text-zinc-500 font-medium mb-4"
        >
          {t("experience.label")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-3xl md:text-4xl font-semibold tracking-tight text-white/90 mb-16"
        >
          {t("experience.title")}
        </motion.h2>

        {/* Paycell Card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          data-testid="experience-paycell-card"
          className="relative bg-[#0F0F10] border border-white/5 rounded-2xl p-8 md:p-12 card-shine border-glow"
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-10">
            <div className="flex items-start gap-5">
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <Building2 size={24} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-white mb-1">Paycell</h3>
                <p className="text-base text-zinc-400">{t("experience.role")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Calendar size={14} strokeWidth={1.5} />
              <span>{t("experience.period")}</span>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-6">
            <p className="text-zinc-400 leading-relaxed max-w-3xl">
              {t("experience.summary")}
            </p>

            {/* Key Projects & Domains */}
            <div>
              <p className="text-sm text-zinc-500 uppercase tracking-wider mb-4">{t("experience.domainsLabel")}</p>
              <div className="flex flex-wrap gap-2">
                {[
                  "Corporate Payments (Lead)",
                  "eKYC & Identity",
                  "Stock & Commodity Trading",
                  "Money Transfers",
                  "Digital Lending",
                  "Insurance Products",
                  "Merchant Onboarding",
                ].map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-800/40 text-zinc-300 border border-zinc-700/50"
                  >
                    {domain}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5">
              <Lock size={16} className="text-zinc-500 mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-zinc-500 leading-relaxed">
                {t("experience.ndaNote")}
              </p>
            </div>

            {/* Responsibilities */}
            <div>
              <p className="text-sm text-zinc-500 uppercase tracking-wider mb-4">{t("experience.contributionsLabel")}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Architected a centralized deep-linking infrastructure and push notification center.",
                  "Spearheaded performance optimizations, reducing build times by 60% and decreasing the overall app size by 80 MB.",
                  "Drove the legacy Objective-C to Swift migration and maintained a 99.7% crash-free rate.",
                  "Overhauled CI/CD pipelines using Jenkins and Fastlane, managing end-to-end App Store deployments.",
                  "Established robust event-driven analytics and monitoring mechanisms integrating Dynatrace, Mixpanel, Firebase, and Netmera.",
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 text-sm text-zinc-400"
                  >
                    <span className="w-1 h-1 rounded-full bg-zinc-600 mt-2 shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Tech tags */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-white/5">
              {["Swift", "SwiftUI", "UIKit", "Objective-C", "MVVM", "VIPER", "Modular Architecture", "SuperApp Architecture", "CI/CD", "Fastlane", "Jenkins", "Tuist", "XCTest", "Test Automation", "Git", "Jira", "Agile", "Scrum Master", "Mixpanel", "Dynatrace", "Firebase", "Netmera", "Deep Linking", "Security Compliance", "Fintech", "Mobile Banking", "Mentorship", "Design Patterns", "Figma"].map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-zinc-400 border border-white/5"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Previous roles - minimal */}
        <div className="mt-8 space-y-4">
          {[
            { company: "Paycell", role: "Senior IOS Developer", period: "2022 - present" },
            { company: "Paycell", role: "IOS Developer", period: "2021 - 2022" },
            { company: "Paycell", role: "Android Developer", period: "2020 - 2021" },
            { company: "Roksit", role: "Web Developer & AI Researcher", period: "2018 - 2019" },
          ].map((job, i) => (
            <motion.div
              key={job.company}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              data-testid={`experience-${job.company.toLowerCase()}-card`}
              className="flex flex-col sm:flex-row sm:items-center justify-between p-6 bg-[#0F0F10] border border-white/5 rounded-2xl hover:border-white/10 transition-colors duration-300"
            >
              <div className="flex items-center gap-4 mb-2 sm:mb-0">
                <div className="w-2 h-2 rounded-full bg-zinc-700" />
                <div>
                  <p className="text-white font-medium">{job.company}</p>
                  <p className="text-sm text-zinc-500">{job.role}</p>
                </div>
              </div>
              <p className="text-sm text-zinc-600 sm:text-right pl-6 sm:pl-0">{job.period}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
